import React, { useState, useMemo } from 'react';
import { Modal, Button, DatePicker, Segmented, Alert, message, Typography } from 'antd';
import { ExclamationCircleOutlined, CalendarOutlined, FieldTimeOutlined, DeleteOutlined } from '@ant-design/icons';
import { Method_POST_CONTEXTO } from '../../../../service/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

dayjs.locale('pt-br');

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ModalDeletePeriod = ({ show, handleClose, onUpdate, zIndex, onFocus }) => {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        show,
        modalKey: 'deletar',
        initialWidth: 520,
        initialHeight: 350,
        overflow: 'visible'
    });
    // Estado para controlar o modo: 'year' ou 'custom'
    const [mode, setMode] = useState('year');

    // dateValue pode ser um objeto dayjs (ano) ou um array [dayjs, dayjs] (período)
    const [dateValue, setDateValue] = useState(null);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    // Gera uma descrição dinâmica do que será apagado para o alerta
    const deleteDescription = useMemo(() => {
        if (!dateValue) return null;

        let start = '';
        let end = '';

        if (mode === 'year') {
            start = dateValue.startOf('year').format('DD/MM/YYYY');
            end = dateValue.endOf('year').format('DD/MM/YYYY');
        } else if (mode === 'custom' && dateValue[0] && dateValue[1]) {
            start = dateValue[0].format('DD/MM/YYYY');
            end = dateValue[1].format('DD/MM/YYYY');
        } else {
            return null;
        }

        return (
            <span>
                Você está prestes a excluir <b>TODOS</b> os lançamentos de <b>{start}</b> até <b>{end}</b>.
            </span>
        );
    }, [dateValue, mode]);

    const handleDelete = async () => {
        if (!dateValue) {
            setErrorMsg('Por favor, selecione o período para excluir.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            let dataInicialStr = '';
            let dataFinalStr = '';

            // Define as datas baseadas no modo escolhido
            if (mode === 'year') {
                dataInicialStr = dateValue.startOf('year').format('YYYY-MM-DD');
                dataFinalStr = dateValue.endOf('year').format('YYYY-MM-DD');
            } else {
                // Modo Custom: respeita exatamente o dia selecionado
                dataInicialStr = dateValue[0].format('YYYY-MM-DD');
                dataFinalStr = dateValue[1].format('YYYY-MM-DD');
            }

            // ATENÇÃO: Ajuste a URL para o endpoint que aceita intervalo de datas ou body JSON
            // Sugestão: Alterar seu backend para receber um JSON com dataInicio e dataTermino
            const url = `/empresa/contabilidade/excluir/periodo`;

            const payload = {
                dataReferencia: dataInicialStr,
                dataTermino: dataFinalStr
            };

            await Method_POST_CONTEXTO(url, payload);

            message.success('Lançamentos excluídos com sucesso!');

            if (onUpdate) onUpdate();
            handleCancel();

        } catch (error) {
            console.error(error);
            let msg = 'Erro ao excluir o período.';
            if (error.response && error.response.data && error.response.data.message) {
                msg = error.response.data.message;
            }
            setErrorMsg(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDateValue(null);
        setErrorMsg(null);
        handleClose();
    };

    return (
        <Modal
            title={
                <span style={{ color: '#ff4d4f' }}>
                    <DeleteOutlined style={{ marginRight: 8 }} />
                    Excluir Lançamentos
                </span>
            }
            open={show}
            onCancel={handleCancel}
            width={500}
            {...draggableProps}
            styles={{ body: { overflow: 'visible' } }}
            footer={[
                <Button key="back" onClick={handleCancel} disabled={loading}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    danger // Botão vermelho para perigo
                    loading={loading}
                    onClick={handleDelete}
                    disabled={!dateValue}
                >
                    Confirmar Exclusão
                </Button>,
            ]}
        >
            {errorMsg && (
                <Alert
                    message="Erro"
                    description={errorMsg}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <div style={{ marginTop: 16 }}>

                <Alert
                    message="Alerta"
                    description={deleteDescription || "Selecione um período para excluir os lançamentos. Esta ação é irreversível."}
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginBottom: 24, border: '1px solid #ffa39e', backgroundColor: '#fff1f0' }}
                />

                <div style={{ backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Selecione o modo de exclusão:</Text>

                    <Segmented
                        block
                        options={[
                            { label: 'Ano Completo', value: 'year', icon: <CalendarOutlined /> },
                            { label: 'Período Específico', value: 'custom', icon: <FieldTimeOutlined /> },
                        ]}
                        value={mode}
                        onChange={(val) => {
                            setMode(val);
                            setDateValue(null); // Limpa valor ao trocar modo
                        }}
                        style={{ marginBottom: 16 }}
                    />

                    {mode === 'year' ? (
                        <DatePicker
                            picker="year"
                            style={{ width: '100%' }}
                            placeholder="Selecione o Ano"
                            format="YYYY"
                            value={dateValue}
                            onChange={setDateValue}
                            getPopupContainer={trigger => trigger.parentElement}
                        />
                    ) : (
                        <RangePicker
                            style={{ width: '100%' }}
                            format={["DD/MM/YYYY", "DDMMYYYY"]}
                            value={dateValue}
                            onChange={setDateValue}
                            placeholder={['Data Inicial', 'Data Final']}
                            getPopupContainer={trigger => trigger.parentElement}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ModalDeletePeriod;